import { setMarkupBidAmount } from './calculation';

const costKeys = [
  'labor_cost',
  'material_cost',
  'equipment_cost',
  'subcontractor_cost',
  'other_cost',
  'direct_cost',
];

const getCosts = (values) => ({
  labor: values.labor_cost,
  material: values.material_cost,
  equipment: values.equipment_cost,
  subcontractor: values.subcontractor_cost,
  other: values.other_cost,
  direct: values.direct_cost,
});

const calculatePercentages = (costs, defaultCosts) => {
  return Object.keys(costs).map((key) => {
    const original = defaultCosts[`${key}_cost`];
    const difference = costs[key] - original;
    return isNaN(difference / original) ? 0 : (difference / original) * 100;
  });
};

const calculateCost = (defaultCosts, percentages) => {
  return Object.keys(defaultCosts).map(
    (key, index) => (defaultCosts[key] * percentages[index]) / 100
  );
};

const calculateTotalPercentage = (total, formData) => {
  const defaultCosts = Object.fromEntries(costKeys.map((key) => [key, Number(formData[key])]));

  return Object.keys(defaultCosts).map((key, index) => {
    const original = defaultCosts[key];
    const difference = total[index] - original;
    return isNaN(difference / original) ? 0 : (difference / original) * 100;
  });
};

const calculatePercentage = (base, value) =>
  base === 0 ? '0.00' : ((value / base) * 100).toFixed(2);

const breakDownCalc = (percentages, costs) => {
  return percentages.map(
    ({
      details,
      labor_percentage,
      material_percentage,
      equipment_percentage,
      subcontractor_percentage,
      other_cost_percentage,
    }) => {
      const labor_markup_cost = (costs.labor * labor_percentage) / 100;
      const material_markup_cost = (costs.material * material_percentage) / 100;
      const equipment_markup_cost = (costs.equipment * equipment_percentage) / 100;
      const subcontractor_markup_cost = (costs.subcontractor * subcontractor_percentage) / 100;
      const other_cost_markup_cost = (costs.other * other_cost_percentage) / 100;
      const total_markup_cost =
        labor_markup_cost +
        material_markup_cost +
        equipment_markup_cost +
        subcontractor_markup_cost +
        other_cost_markup_cost;

      costs.labor += labor_markup_cost;
      costs.material += material_markup_cost;
      costs.equipment += equipment_markup_cost;
      costs.subcontractor += subcontractor_markup_cost;
      costs.other += other_cost_markup_cost;
      costs.direct += total_markup_cost;

      const total_percentage = calculatePercentage(
        costs.direct - total_markup_cost,
        total_markup_cost
      );

      return {
        category: {
          title: details?.description || '',
          values: [
            labor_percentage,
            material_percentage,
            equipment_percentage,
            subcontractor_percentage,
            other_cost_percentage,
            total_percentage,
          ],
        },
        breakdown: [
          {
            title: 'Cost',
            values: [
              labor_markup_cost,
              material_markup_cost,
              equipment_markup_cost,
              subcontractor_markup_cost,
              other_cost_markup_cost,
              total_markup_cost,
            ],
          },
          {
            title: `Cost with ${details?.description || ''}`,
            values: [
              costs.labor,
              costs.material,
              costs.equipment,
              costs.subcontractor,
              costs.other,
              costs.direct,
            ],
          },
        ],
      };
    }
  );
};

const breakDownCalcInSimpleResource = (percentages, costs) => {
  return percentages.map(({ details, percentage_overall }) => {
    const total_markup_cost = (costs.direct * percentage_overall) / 100;

    console.log('check4', total_markup_cost);

    costs.direct += total_markup_cost;

    const totalPercentage = (
      (total_markup_cost / (costs.direct - total_markup_cost)) *
      100
    ).toFixed(2);

    const total_percentage = calculatePercentage(
      costs.direct - total_markup_cost,
      total_markup_cost
    );

    return {
      category: {
        title: details?.description || '',
        values: [0, 0, 0, 0, 0, total_percentage],
      },
      breakdown: [
        {
          title: 'Cost',
          values: [0, 0, 0, 0, 0, total_markup_cost],
        },
        {
          title: `Cost with ${details?.description || ''}`,
          values: [0, 0, 0, 0, 0, costs.direct],
        },
      ],
    };
  });
};

// Reusable functions
export const calculateDefaultPercentages = (totalCosts, costs) => {
  return costs.map((key, index) => {
    return isNaN(costs[index] / totalCosts[index]) ? 0 : (costs[index] / totalCosts[index]) * 100;
  });
};

// Markup
export const CalculateMarkupDetails = (formData, setValue, markupValue) => {
  const markup = markupValue || markupValue === null ? markupValue : formData.markup;
  const { resource_type } = formData;

  const defaultCosts = Object.fromEntries(costKeys.map((key) => [key, Number(formData[key])]));

  const costs = getCosts(defaultCosts);

  const percentages = markup
    ? markup.percentages.map((percentage) => ({
        details: percentage.markup_description,
        ...percentage,
      }))
    : [];

  const breakdownValues =
    resource_type === 'Detailed'
      ? breakDownCalc(percentages, costs)
      : breakDownCalcInSimpleResource(percentages, costs);

  const markupPercentage = calculatePercentages(costs, defaultCosts);

  const markupCost = calculateCost(defaultCosts, markupPercentage);

  const totalAfterMarkup = costKeys.map((key, index) => Number(formData[key]) + markupCost[index]);

  const totalMarkupPercentage = calculateTotalPercentage(totalAfterMarkup, formData);

  setValue('markupDetails', {
    breakdownValues,
    markupPercentage,
    markupCost,
    totalAfterMarkup,
    totalMarkupPercentage,
  });
  CalculateContingencyDetails(formData, setValue, markupCost);
};

// Contingency
export const CalculateContingencyDetails = (formData, setValue, markupCost, contingencyValue) => {
  const contingency =
    contingencyValue || contingencyValue === null ? contingencyValue : formData.contingency;
  const { resource_type, multiplyOnContingency } = formData;
  const { markup } = multiplyOnContingency;

  const defaultCosts = Object.fromEntries(
    costKeys.map((key, index) => [key, Number(formData[key]) + (markup ? markupCost[index] : 0)])
  );

  const costs = getCosts(defaultCosts);

  const percentages = contingency
    ? contingency.percentages.map((percentage) => ({
        details: percentage.contingency_description,
        ...percentage,
      }))
    : [];

  const breakdownValues =
    resource_type === 'Detailed'
      ? breakDownCalc(percentages, costs)
      : breakDownCalcInSimpleResource(percentages, costs);

  const contingencyPercentages = calculatePercentages(costs, defaultCosts);

  const contingencyCost = calculateCost(defaultCosts, contingencyPercentages);

  const totalAfterContingency = costKeys.map(
    (key, index) => Number(formData[key]) + markupCost[index] + contingencyCost[index]
  );

  const totalContingencyPercentage = calculateTotalPercentage(totalAfterContingency, formData);

  setValue('contingencyDetails', {
    breakdownValues,
    contingencyPercentages,
    contingencyCost,
    totalAfterContingency,
    totalContingencyPercentage,
  });
  CalculateEscalationDetails(formData, setValue, markupCost, contingencyCost);
};

// Escalation
export const CalculateEscalationDetails = (
  formData,
  setValue,
  markupCost,
  contingencyCost,
  escalationValue
) => {
  const escalation =
    escalationValue || escalationValue === null ? escalationValue : formData.escalation;
  const { multiplyOnEscalation } = formData;
  const { markup, contingency } = multiplyOnEscalation;

  const defaultCosts = Object.fromEntries(
    costKeys.map((key, index) => [
      key,
      Number(formData[key]) +
        (markup ? markupCost[index] : 0) +
        (contingency ? contingencyCost[index] : 0),
    ])
  );

  const escalationPercentages = Array(6).fill(Number(escalation?.escalation_percentage || 0));

  const escalationCost = calculateCost(defaultCosts, escalationPercentages);

  const totalAfterEscalation = costKeys.map(
    (key, index) =>
      Number(formData[key]) + markupCost[index] + contingencyCost[index] + escalationCost[index]
  );

  const totalEscalationPercentage = calculateTotalPercentage(totalAfterEscalation, formData);

  setValue('escalationDetails', {
    escalationPercentages,
    escalationCost,
    totalAfterEscalation,
    totalEscalationPercentage,
  });
  CalculateTaxDetails(formData, setValue, markupCost, contingencyCost, escalationCost);
};

// Tax
export const CalculateTaxDetails = (
  formData,
  setValue,
  markupCost,
  contingencyCost,
  escalationCost,
  taxValue
) => {
  const tax = taxValue || taxValue === null ? taxValue : formData.tax;
  const { resource_type, multiplyOnTax } = formData;
  const { markup, contingency, escalation } = multiplyOnTax;

  const defaultCosts = Object.fromEntries(
    costKeys.map((key, index) => [
      key,
      Number(formData[key]) +
        (markup ? markupCost[index] : 0) +
        (contingency ? contingencyCost[index] : 0) +
        (escalation ? escalationCost[index] : 0),
    ])
  );

  const costs = getCosts(defaultCosts);

  const percentages = tax
    ? tax.percentages.map((percentage) => ({
        details: percentage.tax_description,
        ...percentage,
      }))
    : [];

  const breakdownValues =
    resource_type === 'Detailed'
      ? breakDownCalc(percentages, costs)
      : breakDownCalcInSimpleResource(percentages, costs);

  const taxPercentages = calculatePercentages(costs, defaultCosts);

  const taxCost = calculateCost(defaultCosts, taxPercentages);

  // const defaultAfterPercentage = costKeys.map(
  //   (key, index) => (taxCost[index] / defaultCosts[key]) * 100
  // );

  // const defaultAfterPercentage = costKeys.map(
  //   (key, index) =>
  //     ((costs[key.replace('_cost', '')] - defaultCosts[key]) / defaultCosts[key]) * 100
  // );

  // console.log(defaultCosts, 'check');
  // console.log(costs, 'check1');
  // console.log(taxCost, 'check2');
  // console.log(defaultAfterPercentage, 'check3');

  const totalAfterTax = costKeys.map(
    (key, index) =>
      Number(formData[key]) +
      markupCost[index] +
      contingencyCost[index] +
      escalationCost[index] +
      taxCost[index]
  );

  const totalTaxPercentage = calculateTotalPercentage(totalAfterTax, formData);

  setValue('taxDetails', {
    breakdownValues,
    taxPercentages,
    taxCost,
    totalAfterTax,
    totalTaxPercentage,
  });
  CalculateDiscountDetails(
    formData,
    setValue,
    markupCost,
    contingencyCost,
    escalationCost,
    taxCost
  );
};

// Discount
export const CalculateDiscountDetails = (
  formData,
  setValue,
  markupCost,
  contingencyCost,
  escalationCost,
  taxCost,
  discountValue
) => {
  const discount = discountValue || discountValue === null ? discountValue : formData.discount;
  const { resource_type, multiplyOnDiscount } = formData;
  const { markup, contingency, escalation, tax } = multiplyOnDiscount;

  const defaultCosts = Object.fromEntries(
    costKeys.map((key, index) => [
      key,
      Number(formData[key]) +
        (markup ? markupCost[index] : 0) +
        (contingency ? contingencyCost[index] : 0) +
        (escalation ? escalationCost[index] : 0) +
        (tax ? taxCost[index] : 0),
    ])
  );

  const costs = getCosts(defaultCosts);

  const percentages = discount
    ? discount.percentages.map((percentage) => ({
        details: percentage.discount_description,
        ...percentage,
      }))
    : [];

  const breakdownValues =
    resource_type === 'Detailed'
      ? breakDownCalc(percentages, costs)
      : breakDownCalcInSimpleResource(percentages, costs);

  const discountPercentages = calculatePercentages(costs, defaultCosts);

  const discountCost = calculateCost(defaultCosts, discountPercentages);

  const totalAfterDiscount = costKeys.map(
    (key, index) =>
      Number(formData[key]) +
      markupCost[index] +
      contingencyCost[index] +
      escalationCost[index] +
      taxCost[index] -
      discountCost[index]
  );

  const totalDiscountPercentage = calculateTotalPercentage(totalAfterDiscount, formData);

  setValue('discountDetails', {
    breakdownValues,
    discountPercentages,
    discountCost,
    totalAfterDiscount,
    totalDiscountPercentage,
  });

  setMarkupBidAmount(totalAfterDiscount[5], formData.quantity, setValue);
};
